import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios, { AxiosHeaders } from 'axios';

import { getCookie } from '@rover/rsdk/src/modules/Network/cookies';

import getCsrfToken from '../getCsrfToken';
import type { AxiosUtilsInterface } from '../types';
import { notifyUnauthorized } from '../unauthorized';
import { isiOS, isMobileBrowser, isMobileEmbedded } from '../userAgent';

import axiosErrorHandler from './axiosErrorHandler';
import createGtmCookiesFromResponse from './createGtmCookiesFromResponse';
import handleCloudflareChallenge from './handleCloudflareChallenge';
import isRoverUrl from './isRoverUrl';
import paramsSerializer from './paramsSerializer';

export const getSourceDescription = (): string => {
  const { userAgent } = navigator;
  if (!userAgent || !userAgent.length || !isMobileBrowser(userAgent)) return 'web'; // If the user agent string doesn't exist, assume this is a web request

  if (!isMobileEmbedded()) return 'mobile_web';
  return isiOS(userAgent) ? 'ios' : 'android';
};

export const getSourceHeader = (): { 'X-Rover-Source': string } => {
  const source = getSourceDescription();
  return {
    'X-Rover-Source': source,
  };
};

export const addRoverHeaders = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const roverConfig = { ...config };
  const token = getCsrfToken();
  const authorization = getCookie('Authorization');

  roverConfig.headers = new AxiosHeaders({
    ...config.headers,
    ...getSourceHeader(),
    ...(token && {
      'X-CSRFToken': token,
    }),
    ...(authorization && {
      Authorization: authorization,
    }),
  });

  return roverConfig;
};

export const createRequestInterceptor =
  () =>
  (defaultConfig: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const isRover = isRoverUrl(defaultConfig.url || '');
    if (!isRover) return defaultConfig; // non-rover urls get NOTHING, to avoid CORS issues

    const configWithHeaders = addRoverHeaders(defaultConfig);
    return configWithHeaders;
  };

export const createResponseInterceptor =
  () =>
  (response: AxiosResponse): AxiosResponse => {
    createGtmCookiesFromResponse(response);
    return response;
  };

// The handler's own probe: excluding it keeps a failure from recursing.
const CURRENT_USER_PATH = '/frontend/current-user/';

/** Reports a Rover-URL 401 to the registered handler and rejects the error unchanged. */
export const createResponseRejectionInterceptor =
  () =>
  (error: AxiosError): Promise<never> => {
    const { config } = error;
    const url = config?.url ?? '';

    const isUnexpectedUnauthorized =
      error.response?.status === 401 &&
      // Mirrors createRequestInterceptor: a third party's 401 says nothing
      // about the Rover session (Lever job listings, presigned S3 uploads).
      isRoverUrl(url) &&
      !url.includes(CURRENT_USER_PATH);

    if (isUnexpectedUnauthorized) {
      notifyUnauthorized({ url, method: config?.method });
    }

    return Promise.reject(error);
  };

export const getAxiosInstance = (): AxiosInstance => {
  const ai = axios.create({
    paramsSerializer,
  });
  ai.interceptors.request.use(createRequestInterceptor(), axiosErrorHandler);
  ai.interceptors.response.use(createResponseInterceptor(), createResponseRejectionInterceptor());
  ai.interceptors.response.use(undefined, handleCloudflareChallenge);
  return ai;
};

const axiosUtils: AxiosUtilsInterface = {
  getAxiosInstance,
};

export default axiosUtils;
